import {
  ChecklistOutlined,
  ChevronRightRounded,
  EastOutlined,
  VisibilityOutlined,
  WestOutlined,
} from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Popper,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil } from "lodash";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { sidebarChecklistSet } from "../../../../redux/actions/sidebarActions";
import {
  sidebarChecklistHideHistory,
  sidebarChecklistHideTemplates,
  sidebarChecklistSetData,
  sidebarChecklistSetId,
  sidebarChecklistSetTemplates,
  sidebarChecklistShowTemplates,
  sidebarChecklistTogglePinned,
} from "../../../../redux/reducers/sidebarReducer";
import AgGridTableWrapper from "../../../agGrid/AgGridTableWrapper";
import ObjectInfoTooltip from "../../../data/ObjectInfoTooltip";
import Flx from "../../../layout/Flx";
import LoadingFallback from "../../../LoadingFallback";
import PinnedModal from "../../../modals/PinnedModal";
import Txt from "../../../typography/Txt";
import ChipItem from "../../../ui/ChipItem";

const LoanChecklistRenderer = lazy(() => import("./LoanChecklistRenderer"));

const LoanChecklist = ({ inline }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();
  const checklistData = useSelector((state) => state.sidebar?.checklist?.data);
  const processingTaskListId = useSelector(
    (state) => state.sidebar?.checklist?.taskListId
  );
  // const [taskListId, setTaskListId] = useState(null);

  const onModalClose = useCallback(() => {
    dispatch(sidebarChecklistTogglePinned());
    // dispatch(sidebarChecklistClear());
  }, [dispatch]);

  const onGoBackToTemplates = useCallback(() => {
    dispatch(sidebarChecklistSetData(null));
    // setTaskListId(null);
  }, [dispatch]);

  const content = (
    <ContentRenderer
      dealId={loanDrilldown?._id}
      taskListId={processingTaskListId}
      onGoBackToTemplates={onGoBackToTemplates}
    />
  );

  // useEffect(() => {
  //   setTaskListId(processingTaskListId);
  // }, [processingTaskListId]);
  useEffect(() => {
    if (isNil(loanDrilldown?.processingTaskListId)) {
      dispatch(sidebarChecklistShowTemplates());
    } else {
      dispatch(sidebarChecklistSetId(loanDrilldown?.processingTaskListId));
    }
  }, []);

  if (inline) {
    return content;
  }

  return (
    <PinnedModal
      show
      title={`${checklistData?.checklistName} Checklist` || "Loan Checklist"}
      titleEndContent={
        <ObjectInfoTooltip
          title="Template Details"
          format={[{ field: "createdDateTime", type: "date" }]}
          data={checklistData}
        />
      }
      onClose={onModalClose}
      fixed
      inline={inline}
      position="relative"
      icon={<ChecklistOutlined className="thin" />}
      height="880px"
      maxWidth="920px"
    >
      {/* <Box sx={{ p: 2 }}>{content}</Box> */}
      <Box sx={{ px: 2 }}>{content}</Box>
    </PinnedModal>
  );
};

const ContentRenderer = ({ dealId, taskListId, onGoBackToTemplates }) => {
  const checklist = useSelector((state) => state.sidebar?.checklist);
  const showTemplates = checklist?.showTemplates;
  const checklistData = checklist?.data;
  const showHistory = checklist?.showHistory;

  if (showTemplates) {
    return <TemplateSelector dealId={dealId} />;
  }

  if (showHistory && !isEmpty(checklistData)) {
    return <ChecklistChangeHistory dealId={dealId} />;
  }

  if (taskListId) {
    return (
      <ChecklistContent
        dealId={dealId}
        taskListId={taskListId}
        // taskListId={processingTaskListId}
        onGoBackToTemplates={onGoBackToTemplates}
      />
    );
  }
  return (
    <Flx column gap={2} center sx={{ p: 4, height: "300px" }}>
      <CircularProgress />
      <Txt secondary>Getting checklist data</Txt>
    </Flx>
  );
};

const ChecklistContent = ({ dealId, taskListId, onGoBackToTemplates }) => {
  const checklist = useSelector((state) => state.sidebar.checklist?.data);
  const checklistData = checklist?.taskList;

  const { getTaskListById } = useChecklistRouteHook();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isNil(checklistData)) {
      getTaskListById({
        dealId,
        taskListId,
        onSuccessFn: (data) => {
          // console.log("Checklist task lists", data);
          dispatch(sidebarChecklistSetData(data));
        },
      });
    }
  }, [taskListId]);

  if (isNil(checklistData)) {
    return (
      <Flx column gap={2} center sx={{ p: 4, height: "300px" }}>
        <CircularProgress />
        <Txt secondary>Getting checklist data</Txt>
      </Flx>
    );
  }

  // return (
  //   <Flx column>
  //     <UpdateTaskDataButton />
  //     <LoanChecklistRenderer
  //       data={checklistData}
  //       onItemUpdate={handleItemUpdate}
  //     />

  //   </Flx>
  // );

  return (
    <Suspense fallback={<LoadingFallback sx={{ py: 10 }} />}>
      <LoanChecklistRenderer
        data={checklistData}
        dealId={dealId}
        taskListId={taskListId}
      />
    </Suspense>
  );
};

/**
 * #################################
 * TEMPLATE RELATED
 * #################################
 */
const TemplateSelector = ({ dealId }) => {
  const dispatch = useDispatch();
  const taskListId = useSelector(
    (state) => state.sidebar?.checklist?.taskListId
  );

  return (
    <Flx column gap={2} sx={{ p: 4 }}>
      <Flx center column sx={{ position: "relative" }}>
        {isNil(taskListId) ? null : (
          <Button
            // color="inherit"
            color="initial"
            sx={{ position: "absolute", top: 8, left: 0 }}
            startIcon={<WestOutlined className="thin" />}
            onClick={() => {
              dispatch(sidebarChecklistHideHistory());
              dispatch(sidebarChecklistHideTemplates());
            }}
          >
            Back To Checklist
          </Button>
        )}
        <Txt sx={{ fontSize: "16px", fontWeight: 500 }}>Choose a Template</Txt>
        <Txt secondary>Pick a template to get started quickly</Txt>
      </Flx>
      <TemplateSelectorCardRenderer dealId={dealId} />
    </Flx>
  );
};

const TemplateSelectorCardRenderer = ({ dealId }) => {
  const { getChecklistTasksList } = useChecklistRouteHook();
  const templates = useSelector((state) => state.sidebar.checklist?.templates);
  const showTemplates = useSelector(
    (state) => state.sidebar?.checklist?.showTemplates
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (isNil(templates) && showTemplates) {
      getChecklistTasksList({
        onSuccessFn: (data) => {
          dispatch(sidebarChecklistSetTemplates(data));
        },
      });
    }
  }, [templates]);

  if (isNil(templates)) {
    return (
      <Flx center sx={{ p: 3 }}>
        <CircularProgress />
      </Flx>
    );
  }

  return (
    <FlxGrid width="400px" grow={1} gap={2}>
      {templates?.map((template) => (
        <TemplateCard key={template._id} template={template} dealId={dealId} />
      ))}
    </FlxGrid>
  );
};

const TemplateCard = ({ dealId, template }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { createNewTaskList, loading } = useChecklistRouteHook();
  const dispatch = useDispatch();

  const onTemplateCreate = useCallback(() => {
    createNewTaskList({
      dealId,
      taskListId: template?._id,
      onSuccessFn: (data) => {
        console.log("Checklist task lists created", data);
        dispatch(sidebarChecklistSet(data));
        dispatch(sidebarChecklistSetId(data?._id));
        const updated = { ...loanDrilldown, processingTaskListId: data?._id };
        dispatch(loanDrilldownSet(updated));
      },
    });
  }, [template, dealId, createNewTaskList, dispatch]);

  return (
    <Card
      sx={{
        flexBasis: "340px",
        flexGrow: 1,
        flexShrink: 1,
      }}
    >
      <Flx column gap={1}>
        <Flx column gap={1}>
          <Flx ac gap={0.5}>
            <Txt
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                color: grey[900],
              }}
            >
              {template?.checklistName}
            </Txt>
            <TemplatePreviewButton template={template} />
          </Flx>
          <Flx column gap={0.4}>
            <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
              Version: {template?.checklistVersion}
            </Txt>
            <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
              Created By: {template?.fileCreatedBy}
            </Txt>
            <Txt sx={{ fontSize: "10px" }} secondary fontStyle="italic">
              File Source: {template?.fileSource}
            </Txt>
          </Flx>
        </Flx>
        <Flx ac end gap={1}>
          <Button
            loading={loading}
            endIcon={<EastOutlined className="thin" />}
            onClick={onTemplateCreate}
            variant="text"
            disabled={loading}
          >
            Create Checklist
          </Button>
        </Flx>
      </Flx>
    </Card>
  );
};

const TemplatePreviewButton = ({ template }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "template-preview-popper" : undefined;
  const taskList = template?.taskList || {};
  const taskListKeys = Object.keys(taskList);

  return (
    <Box sx={{ position: "relative" }}>
      <Backdrop
        open={open}
        onClick={handleClick}
        sx={{ backgroundColor: "transparent" }}
      />
      <Popper sx={{ zIndex: 12100 }} open={open} id={id} anchorEl={anchorEl}>
        <Card sx={{ p: 0, width: "300px" }}>
          <Box sx={{ p: 2, borderBottom: "1px solid #eee" }}>
            <Txt
              sx={{
                fontWeight: 600,
                fontSize: "13px",
                color: grey[900],
              }}
            >
              {template?.checklistName}
            </Txt>
          </Box>
          <Flx column sx={{ maxHeight: "480px", overflowY: "auto" }}>
            {taskListKeys?.map((key) => (
              <ExpandableSection key={key}>{key}</ExpandableSection>
            ))}
          </Flx>
        </Card>
      </Popper>
      <Tooltip title="Preview Template">
        <IconButton
          size="small"
          aria-describedby={id}
          onClick={handleClick}
          variant="text"
          color="gray"
        >
          <VisibilityOutlined className="thin" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ExpandableSection = ({ children }) => {
  const [show, setShow] = useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1,
        borderBottom: "1px solid #eee",
        cursor: "pointer",
      }}
      onClick={() => setShow((prev) => !prev)}
    >
      <ChevronRightRounded
        className="thin"
        sx={{
          transition: "100ms",
          transform: `rotate(${show ? 90 : 0}deg)`,
        }}
      />
      <Txt>{children}</Txt>
    </Box>
  );
};

/**
 * #################################
 * CHANGELONG RELATED
 * #################################
 */
const ChecklistChangeHistory = ({ dealId }) => {
  const checklist = useSelector((state) => state.sidebar?.checklist);

  const taskListUpdateHistory = checklist?.data?.taskListUpdateHistory || [];

  const dispatch = useDispatch();
  const taskListId = useSelector(
    (state) => state.sidebar?.checklist?.taskListId
  );

  return (
    <Flx column gap={2} sx={{ p: 4 }}>
      <Flx center column sx={{ position: "relative" }}>
        {isNil(taskListId) ? null : (
          <Button
            // color="inherit"
            color="initial"
            sx={{ position: "absolute", top: 8, left: 0 }}
            startIcon={<WestOutlined className="thin" />}
            onClick={() => {
              dispatch(sidebarChecklistHideHistory());
              dispatch(sidebarChecklistHideTemplates());
            }}
          >
            Back To Checklist
          </Button>
        )}
        <Txt sx={{ fontSize: "16px", fontWeight: 500 }}>Update History</Txt>
        <Txt secondary>View the history of updates made to this checklist</Txt>
      </Flx>
      <LoanChecklistUpdateHistoryTable rowData={taskListUpdateHistory} />
    </Flx>
  );
};

const LoanChecklistUpdateHistoryTable = ({
  quickFilter,
  rowData,
  showDocGroup = false,
  groupDocGroups = false,
  showFileMetadata = false,
  height,
  domLayout = "autoHeight", // normal | autoHeight
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const checklist = useSelector((state) => state.sidebar?.checklist);
  const taskList = checklist?.data?.taskList || [];
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Updated Group & Condition",
        field: "taskId",
        autoHeight: true,
        wrapText: true,
        flex: 1,
        // minWidth: 200,
        // valueFormatter: (params) => {
        //   const filteredItem = taskList.find((t) => t._id === params?.value);
        //   if (filteredItem) {
        //     return filteredItem?.taskGroup || "N/A";
        //   }
        //   return params?.value?.taskName || "N/A";
        // },

        cellRenderer: (params) => {
          const filteredItem = taskList.find((t) => t._id === params?.value);
          if (filteredItem) {
            return (
              <Flx column sx={{ height: "100%", pt: 0.6 }} jc fw>
                <Txt sx={{ fontSize: "11px" }} bold>
                  {filteredItem?.taskGroup || "N/A"}
                </Txt>
                <Txt secondary sx={{ fontSize: "11px" }}>
                  {filteredItem?.taskCondition || "N/A"}
                </Txt>
              </Flx>
            );
          }
          return params?.value?.taskName || "N/A";
        },
      },
      // {
      //   headerName: "Name",
      //   field: "taskId",
      //   flex: 1,
      //   minWidth: 250,
      //   width: 300,
      //   autoHeight: true,
      //   wrapText: true,
      //   cellStyle: {
      //     lineHeight: 1.5,
      //     paddingTop: "8px",
      //     paddingBottom: "8px",
      //     // whiteSpace: "normal",
      //   },
      //   valueFormatter: (params) => {
      //     const filteredItem = taskList.find((t) => t._id === params?.value);
      //     if (filteredItem) {
      //       return filteredItem?.taskCondition || "N/A";
      //     }
      //     return params?.value?.taskName || "N/A";
      //   },
      // },

      {
        headerName: "Updated Field",
        field: "updatePayload",
        autoHeight: true,
        wrapText: true,
        flex: 0, // Add flex to allow column to grow
        // minWidth: 200, // Set minimum width
        cellStyle: {
          whiteSpace: "normal", // Enable text wrapping
          wordBreak: "break-word", // Break long words
          lineHeight: "1.4", // Better line spacing
          padding: "8px", // Add padding for better readability
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start", // Use flex-start instead of start for better compatibility
        },

        cellRenderer: (params) => {
          // const = [{statis :'asd'},{statis :'asd'}]
          const fields = Object.keys(params?.value) || [];
          // const fields = ["status", "assignedTo", "dueDate"];
          return fields?.map((f) => {
            return <ChipItem value={`${f}: ${params?.value[f] || "N/A"}`} />;
          });
        },
        // cellRenderer: (params) => {
        //   // const fields = [{ status: "asd" }, { staasdtus: "ggsasd" }];
        //   const fields = Object.keys(params?.value) || [];

        //   return (
        //     <div
        //       style={{
        //         display: "flex",
        //         flexDirection: "column",
        //         gap: "4px",
        //         width: "100%",
        //       }}
        //     >
        //       {fields?.map((field, index) => {
        //         const key = Object.keys(field)[0];
        //         const value = field[key];
        //         return (
        //           <ChipItem
        //             key={index}
        //             value={`${key}: ${params?.value?.[key] || value || "N/A"}`}
        //           />
        //         );
        //       })}
        //     </div>
        //   );
        // },
      },

      {
        headerName: "Updated On",
        field: "updateDateTime",
        sort: "desc",
        width: 165,
        type: "dateTime",
      },
      {
        headerName: "Updated By",
        field: "update_user",

        width: 140,

        // cellRenderer: (params) => {
        //   return (
        //     <Flx column>
        //       <Txt>
        //         {params?.value?.fullName || params?.value?.emailAddress}
        //       </Txt>
        //     </Flx>
        //   );
        //   // return (
        //   //   params?.value?.fullName ||
        //   //   `${params?.value?.firstName} ${params?.value?.lastName}`
        //   // );
        // },

        valueFormatter: (params) => {
          return (
            params?.value?.fullName ||
            `${params?.value?.firstName} ${params?.value?.lastName}`
          );
        },
      },
    ],
    [taskList]
  );

  const defaultColDef = useMemo(
    () => ({
      filter: true,
      enableRowGroup: true,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      // minWidth: 200,
      // cellClass: "center",
    }),
    []
  );
  const onFirstDataRendered = useCallback((params) => {
    // agApiSizeColumnsToFit(params);
    // agApiAutoSizeColumns(params);
  }, []);

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
    };
  }, []);

  if (isEmpty(rowData)) {
    return (
      <Box sx={{ p: 2 }}>
        <Txt secondary>No changes made</Txt>
      </Box>
    );
  }

  return (
    <AgGridTableWrapper
      simpleTable
      sx={{ mt: 0.5 }}
      height={height}
      suppressMinHeightWhenPopulated={rowData}
    >
      <AgGridReact
        ref={ref}
        rowData={rowData}
        columnDefs={columnDefs}
        quickFilterText={quickFilter}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        // headerHeight={0}
        onFirstDataRendered={onFirstDataRendered}
        domLayout={domLayout}
        enableCellTextSelection
      />
    </AgGridTableWrapper>
  );
};

const checklistData = [
  {
    _id: "68d2a8dfbd67bbd3ebcf17da",
    status: "Completed",
    taskGroup: "Application",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm there is a loan application on file for each borrower",
    loanLevelCondition: "Loan Application: Missing",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17db",
    status: "Exception Requested",
    taskGroup: "Application",
    stageCollected: "Underwriting",
    taskCondition:
      "If borrower's primary residence address (Zestimate) is below the value of the subject investment property then flag",
    loanLevelCondition:
      "Occupancy Concerns - Loan Application indicates primary residence's value is below subject investment property's value",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17dc",
    status: "Exception Requested",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition: "Confirm there is an appraisal on file for each property",
    loanLevelCondition: "Appraisal: Missing",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17dd",
    status: "In Progress",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm the appraisal is not marked rural or eligible under the USDA website",
    loanLevelCondition: "Appraisal: Property location is marked Rural",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17de",
    status: "Completed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm the appraisal is ordered either by a Correspondent or internally",
    loanLevelCondition: "Appraisal: Report is ordered by unauthorized party",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17df",
    status: "Completed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm there are no physical deficiencies for a Bridge or DSCR loan",
    loanLevelCondition:
      "Appraisal: Physical deficiencies noted by appraiser for Bridge RTL/MF or DSCR loan",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e0",
    status: "Not Reviewed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm As-Is Value, Rehab Budget (if applicable) and ARV (if applicable) match the Loan Sizer for RTL/GUC/MF loan",
    loanLevelCondition:
      "Loan Sizer: As-Is/Budget/ARV in Loan Sizer are incorrect for RTL/GUC/MF loan according to Appraisal",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e3",
    status: "Completed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm that the occupancy timing matches the appraisal photos for DSCR loans",
    loanLevelCondition:
      "Valuation Concerns - Effective property occupancy date (per Lease Agreement) and effective valuation date mismatch",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e4",
    status: "Completed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm that the comps are similar to our subject investment property",
    loanLevelCondition:
      "Valuation Concerns -  Selected comps are NOT similar to the subject investment property",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e5",
    status: "Not Reviewed",
    taskGroup: "Appraisal",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm that the subject investment property's price point makes sense relative to the neighborhood",
    loanLevelCondition:
      "Valuation Concerns - Subject investment property's price point does NOT makes sense relative to the neighborhood",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e6",
    status: "Not Reviewed",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm there is a Background Report on file for each borrower (100% of the entity)",
    loanLevelCondition: "Background Report: Missing",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e7",
    status: "In Progress",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm no Foreclosure, Short Sale or Deed in Lieu in the past 2 years",
    loanLevelCondition:
      "Background Report: Derogatory event in the past 2 years",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e8",
    status: "Completed",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition: "Confirm no Bankruptcy in the past 4 years",
    loanLevelCondition: "Background Report: Bankruptcy in the past 4 years",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17e9",
    status: "Failed",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm background report does not mention any criminal/felony hits",
    loanLevelCondition: "Background Report: Felony hits reported",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17ea",
    status: "Exception Requested",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm background report does NOT include the seller, tenant and any other related party",
    loanLevelCondition:
      "Non-Arm's Length Transaction - Background Report indicates relationship between borrower, seller or tenant",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17eb",
    status: "Not Applicable",
    taskGroup: "Background Report",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm background report does NOT mention investment property address as primary residence or previous primary residence",
    loanLevelCondition:
      "Occupancy Concerns - Background Report states subject investment property as primary residence or previously owned as primary residence",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17ec",
    status: "Completed",
    taskGroup: "Budget",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm there is a Rehab Budget on file if there is rehab/construction contemplated",
    loanLevelCondition: "Budget: Missing",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17ed",
    status: "Completed",
    taskGroup: "Budget",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm the budget is addressing all the property deficiencies that are present in the appraisal",
    loanLevelCondition:
      "Budget: Not addressing deficiencies present in the appraisal",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "Internal",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
  {
    _id: "68d2a8dfbd67bbd3ebcf17ee",
    status: "Not Applicable",
    taskGroup: "Condo Questionnaire",
    stageCollected: "Underwriting",
    taskCondition:
      "Confirm there is a Condo Questionnaire on file if the property is a Condominium",
    loanLevelCondition: "Condo Questionnaire: Missing ",
    reviewerProfile: "David Russo & Co",
    responsibleProvidingParty: "External",
    checklist_title: "Pre-Close LIGHT",
    applicable_products: [
      "30 Year DSCR Rental",
      "Bridge",
      "Fix and Flip",
      "Ground Up Construction",
      "5+ Units",
    ],
    updateDateTime: null,
    createdDateTime: "2025-09-25T06:19:32.513000",
    completedDateTime: null,
    completedBy: null,
    dueDateTime: null,
    rescheduledDate: null,
    rescheduleCount: 0,
    comments: null,
    taskCompletionUser: null,
    end_of_week_date: null,
  },
];
export default LoanChecklist;
